'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/lib/authContext';
import { UserRole } from '@/lib/mockData';

type AuthMode = 'login' | 'register-client' | 'register-contractor' | 'role-select';

interface LoginForm { email: string; password: string; remember: boolean; }
interface ClientRegForm { fullName: string; email: string; phone: string; location: string; password: string; confirmPassword: string; terms: boolean; }
interface ContractorRegForm { companyName: string; contactPerson: string; email: string; phone: string; registrationNumber: string; yearsExperience: number; mainServices: string; serviceLocations: string; password: string; confirmPassword: string; terms: boolean; }

export default function AuthPageContent() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginAsDemo } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const clientForm = useForm<ClientRegForm>();
  const contractorForm = useForm<ContractorRegForm>();

  function routeAfterLogin(role: UserRole) {
    if (role === 'client') router.push('/client-dashboard');
    else if (role === 'contractor') router.push('/contractor-dashboard');
    else router.push('/admin-dashboard');
  }

  async function handleLogin(data: LoginForm) {
    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    const result = login(data.email, data.password);
    setLoading(false);
    if (result.success && result.role) {
      toast.success('Welcome back to Robeli!');
      routeAfterLogin(result.role);
    } else {
      loginForm.setError('email', { message: result.error });
    }
  }

  async function handleDemoLogin(role: UserRole) {
    setLoading(true);
    await new Promise(r => setTimeout(r, 600));
    loginAsDemo(role);
    setLoading(false);
    toast.success(`Signed in as Demo ${role.charAt(0).toUpperCase() + role.slice(1)}`);
    routeAfterLogin(role);
  }

  async function handleClientReg(data: ClientRegForm) {
    if (data.password !== data.confirmPassword) {
      clientForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    loginAsDemo('client');
    setLoading(false);
    toast.success('Account created! Welcome to Robeli.');
    router.push('/client-dashboard');
  }

  async function handleContractorReg(data: ContractorRegForm) {
    if (data.password !== data.confirmPassword) {
      contractorForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    loginAsDemo('contractor');
    setLoading(false);
    toast.success('Application submitted — awaiting administrator approval.');
    router.push('/contractor-dashboard');
  }

  const inputClass = (error?: boolean) => `input-field ${error ? 'input-error' : ''}`;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 gradient-hero flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 blob-navy opacity-40" />
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={40} />
            <span className="font-bold text-2xl text-white">Robeli</span>
          </Link>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
            The smarter way to manage your construction project.
          </h2>
          <p className="text-white/70 text-base mb-8 leading-relaxed">
            From design to final handover — compare quotations, pay by milestone, and track every stage in one place.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: '600+', label: 'Happy Clients' },
              { value: '48', label: 'Verified Contractors' },
              { value: 'R192M+', label: 'Projects Managed' },
              { value: '4.8★', label: 'Average Rating' },
            ].map(s => (
              <div key={`ls-${s.label}`} className="bg-white/10 rounded-xl p-3 border border-white/20">
                <p className="text-xl font-bold text-white font-tabular">{s.value}</p>
                <p className="text-xs text-white/60 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/40">
          © 2026 Robeli (Pty) Ltd · All rights reserved
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-primary">Robeli</span>
          </div>

          {/* ── LOGIN ─────────────────────────────────────────────────────────── */}
          {mode === 'login' && (
            <div className="fade-in">
              <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back</h1>
              <p className="text-muted-foreground text-sm mb-7">Sign in to your Robeli account</p>

              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                  <input
                    type="email"
                    {...loginForm.register('email', { required: 'Email is required' })}
                    className={inputClass(!!loginForm.formState.errors.email)}
                    placeholder="you@example.com"
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-xs text-destructive mt-1">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...loginForm.register('password', { required: 'Password is required' })}
                      className={`${inputClass(!!loginForm.formState.errors.password)} pr-10`}
                      placeholder="Enter your password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-xs text-destructive mt-1">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...loginForm.register('remember')} className="w-4 h-4 rounded border-border accent-primary" />
                    <span className="text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button type="button" className="text-sm text-primary font-medium hover:underline">Forgot password?</button>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3">
                  {loading ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : <Icon name="ArrowRightOnRectangleIcon" size={16} />}
                  {loading ? 'Signing In...' : 'Log In'}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-muted-foreground font-medium">or use demo access</span></div>
              </div>

              {/* Demo Access */}
              <div className="bg-secondary rounded-xl p-4 border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="BoltIcon" size={14} className="text-accent" />
                  <span className="text-xs font-bold text-foreground">Demo Access — No Registration Required</span>
                </div>
                <div className="space-y-2 mb-4">
                  {[
                    { role: 'client' as UserRole, label: 'Continue as Demo Client', icon: 'HomeIcon', color: 'bg-primary text-primary-foreground' },
                    { role: 'contractor' as UserRole, label: 'Continue as Demo Contractor', icon: 'WrenchScrewdriverIcon', color: 'bg-success text-success-foreground' },
                    { role: 'admin' as UserRole, label: 'Continue as Demo Administrator', icon: 'ShieldCheckIcon', color: 'bg-accent text-accent-foreground' },
                  ].map(d => (
                    <button
                      key={`demo-${d.role}`}
                      onClick={() => handleDemoLogin(d.role)}
                      disabled={loading}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 hover:opacity-90 active:scale-98 ${d.color}`}
                    >
                      <Icon name={d.icon as any} size={15} />
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="border-t border-border pt-3 space-y-1">
                  <p className="text-[10px] font-bold text-muted-foreground mb-1.5 uppercase tracking-wide">Demo Credentials</p>
                  {[
                    { role: 'Client', email: 'client@demo.com' },
                    { role: 'Contractor', email: 'contractor@demo.com' },
                    { role: 'Admin', email: 'admin@demo.com' },
                  ].map(c => (
                    <div key={`cred-${c.role}`} className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-medium">{c.role}:</span>
                      <span className="font-mono text-foreground">{c.email} / demo123</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Don't have an account?{' '}
                <button onClick={() => setMode('role-select')} className="text-primary font-semibold hover:underline">Register here</button>
              </p>
            </div>
          )}

          {/* ── ROLE SELECT ────────────────────────────────────────────────────── */}
          {mode === 'role-select' && (
            <div className="fade-in">
              <h1 className="text-2xl font-bold text-foreground mb-1">Create Your Account</h1>
              <p className="text-muted-foreground text-sm mb-8">How will you be using Robeli?</p>
              <div className="space-y-4">
                <button
                  onClick={() => setMode('register-client')}
                  className="w-full card-elevated card-hover p-5 text-left flex items-start gap-4 border-2 hover:border-primary transition-colors"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                    <Icon name="HomeIcon" size={22} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-0.5">Register as a Client</h3>
                    <p className="text-sm text-muted-foreground">I'm a property owner looking to hire a contractor for a construction or renovation project.</p>
                  </div>
                  <Icon name="ChevronRightIcon" size={18} className="text-muted-foreground mt-1 shrink-0" />
                </button>
                <button
                  onClick={() => setMode('register-contractor')}
                  className="w-full card-elevated card-hover p-5 text-left flex items-start gap-4 border-2 hover:border-primary transition-colors"
                >
                  <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center shrink-0">
                    <Icon name="WrenchScrewdriverIcon" size={22} className="text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-0.5">Register as a Contractor</h3>
                    <p className="text-sm text-muted-foreground">I'm a construction company or tradesperson looking to receive project enquiries and manage my work on the platform.</p>
                  </div>
                  <Icon name="ChevronRightIcon" size={18} className="text-muted-foreground mt-1 shrink-0" />
                </button>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-primary font-semibold hover:underline">Log In</button>
              </p>
            </div>
          )}

          {/* ── CLIENT REGISTER ─────────────────────────────────────────────────── */}
          {mode === 'register-client' && (
            <div className="fade-in">
              <button onClick={() => setMode('role-select')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back
              </button>
              <h1 className="text-2xl font-bold text-foreground mb-1">Create Client Account</h1>
              <p className="text-muted-foreground text-sm mb-6">Tell us about yourself to get started.</p>
              <form onSubmit={clientForm.handleSubmit(handleClientReg)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
                  <input type="text" {...clientForm.register('fullName', { required: 'Full name is required' })} className={inputClass(!!clientForm.formState.errors.fullName)} placeholder="Jane Smith" />
                  {clientForm.formState.errors.fullName && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.fullName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                  <input type="email" {...clientForm.register('email', { required: 'Email is required' })} className={inputClass(!!clientForm.formState.errors.email)} placeholder="jane@example.com" />
                  {clientForm.formState.errors.email && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.email.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
                    <input type="tel" {...clientForm.register('phone', { required: 'Phone is required' })} className={inputClass(!!clientForm.formState.errors.phone)} placeholder="+27 82 000 0000" />
                    {clientForm.formState.errors.phone && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.phone.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Location</label>
                    <input type="text" {...clientForm.register('location', { required: 'Location is required' })} className={inputClass(!!clientForm.formState.errors.location)} placeholder="Sandton, Johannesburg" />
                    {clientForm.formState.errors.location && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.location.message}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} {...clientForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} className={`${inputClass(!!clientForm.formState.errors.password)} pr-10`} placeholder="Minimum 8 characters" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {clientForm.formState.errors.password && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.password.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <input type={showConfirm ? 'text' : 'password'} {...clientForm.register('confirmPassword', { required: 'Please confirm your password' })} className={`${inputClass(!!clientForm.formState.errors.confirmPassword)} pr-10`} placeholder="Repeat your password" />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {clientForm.formState.errors.confirmPassword && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.confirmPassword.message}</p>}
                </div>
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" {...clientForm.register('terms', { required: 'You must accept the terms' })} className="mt-0.5 w-4 h-4 accent-primary" />
                    <span className="text-sm text-muted-foreground">I accept the <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a></span>
                  </label>
                  {clientForm.formState.errors.terms && <p className="text-xs text-destructive mt-1">{clientForm.formState.errors.terms.message}</p>}
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3">
                  {loading ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : null}
                  {loading ? 'Creating Account...' : 'Create Client Account'}
                </button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-primary font-semibold hover:underline">Log In</button>
              </p>
            </div>
          )}

          {/* ── CONTRACTOR REGISTER ─────────────────────────────────────────────── */}
          {mode === 'register-contractor' && (
            <div className="fade-in">
              <button onClick={() => setMode('role-select')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back
              </button>
              <h1 className="text-2xl font-bold text-foreground mb-1">Register as a Contractor</h1>
              <p className="text-muted-foreground text-sm mb-2">Your application will be reviewed by our team before approval.</p>
              <div className="bg-warning/10 border border-warning/30 rounded-lg px-3 py-2 mb-5 flex items-center gap-2">
                <Icon name="ClockIcon" size={14} className="text-warning shrink-0" />
                <p className="text-xs text-warning font-medium">Verification typically takes 2-3 business days</p>
              </div>
              <form onSubmit={contractorForm.handleSubmit(handleContractorReg)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Company Name</label>
                  <input type="text" {...contractorForm.register('companyName', { required: 'Company name is required' })} className={inputClass(!!contractorForm.formState.errors.companyName)} placeholder="Apex Build Solutions Ltd" />
                  {contractorForm.formState.errors.companyName && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.companyName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Contact Person</label>
                  <input type="text" {...contractorForm.register('contactPerson', { required: 'Contact person is required' })} className={inputClass(!!contractorForm.formState.errors.contactPerson)} placeholder="Marcus Thompson" />
                  {contractorForm.formState.errors.contactPerson && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.contactPerson.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                  <input type="email" {...contractorForm.register('email', { required: 'Email is required' })} className={inputClass(!!contractorForm.formState.errors.email)} placeholder="info@apexbuildsolutions.co.za" />
                  {contractorForm.formState.errors.email && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.email.message}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
                    <input type="tel" {...contractorForm.register('phone', { required: 'Phone is required' })} className={inputClass(!!contractorForm.formState.errors.phone)} placeholder="+27 11 000 0000" />
                    {contractorForm.formState.errors.phone && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.phone.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Years of Experience</label>
                    <input type="number" {...contractorForm.register('yearsExperience', { required: 'Required', min: { value: 1, message: 'Min 1 year' } })} className={inputClass(!!contractorForm.formState.errors.yearsExperience)} placeholder="15" />
                    {contractorForm.formState.errors.yearsExperience && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.yearsExperience.message}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Business Registration Number</label>
                  <input type="text" {...contractorForm.register('registrationNumber', { required: 'Registration number is required' })} className={inputClass(!!contractorForm.formState.errors.registrationNumber)} placeholder="GB-REG-1234567" />
                  {contractorForm.formState.errors.registrationNumber && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.registrationNumber.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Main Services</label>
                  <p className="text-xs text-muted-foreground mb-1">Separate multiple services with commas</p>
                  <input type="text" {...contractorForm.register('mainServices', { required: 'Please list your main services' })} className={inputClass(!!contractorForm.formState.errors.mainServices)} placeholder="Full Renovation, Kitchen, Bathroom, Extensions" />
                  {contractorForm.formState.errors.mainServices && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.mainServices.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Service Locations</label>
                  <input type="text" {...contractorForm.register('serviceLocations', { required: 'Please list your service locations' })} className={inputClass(!!contractorForm.formState.errors.serviceLocations)} placeholder="Johannesburg, Cape Town, Pretoria" />
                  {contractorForm.formState.errors.serviceLocations && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.serviceLocations.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} {...contractorForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} className={`${inputClass(!!contractorForm.formState.errors.password)} pr-10`} placeholder="Minimum 8 characters" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {contractorForm.formState.errors.password && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.password.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
                  <input type={showConfirm ? 'text' : 'password'} {...contractorForm.register('confirmPassword', { required: 'Please confirm your password' })} className={inputClass(!!contractorForm.formState.errors.confirmPassword)} placeholder="Repeat your password" />
                  {contractorForm.formState.errors.confirmPassword && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.confirmPassword.message}</p>}
                </div>
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" {...contractorForm.register('terms', { required: 'You must accept the terms' })} className="mt-0.5 w-4 h-4 accent-primary" />
                    <span className="text-sm text-muted-foreground">I accept the <a href="#" className="text-primary hover:underline">Terms of Service</a>, <a href="#" className="text-primary hover:underline">Contractor Agreement</a>, and <a href="#" className="text-primary hover:underline">Privacy Policy</a></span>
                  </label>
                  {contractorForm.formState.errors.terms && <p className="text-xs text-destructive mt-1">{contractorForm.formState.errors.terms.message}</p>}
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3">
                  {loading ? <Icon name="ArrowPathIcon" size={16} className="animate-spin" /> : null}
                  {loading ? 'Submitting Application...' : 'Submit Contractor Application'}
                </button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-primary font-semibold hover:underline">Log In</button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}